import type { ReactNode } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { Bot, AlertCircle } from "lucide-react";
import { extractMessageText, isHiddenControlMessage } from "@/shared/lib/uiMessageUtils";
import { EditPromptHint } from "./ChatInlineMediaBlocks";
import { renderChatMessagePart, type ChatToolPartRenderer } from "./ChatMessagePart";
import { ChatTypingIndicator } from "./ChatThinkingIndicators";
import { messageHasGeneratedMedia } from "./chatMarkdownUtils";
import { SentContextBadges } from "./SentContextBadges";

interface ChatMessageListProps {
  messages: UIMessage[];
  persona?: { humanName: string; humanVideo?: string };
  avatarEnabled?: boolean;
  isLoading?: boolean;
  chatError?: Error;
  /** Called when user clicks the retry button on error */
  onRetry?: () => void;
  /** Compact mode: smaller bubbles, plain text, no tool cards */
  compact?: boolean;
  /** Custom renderer for static tool parts (type: "tool-{name}") */
  renderToolPart?: ChatToolPartRenderer;
  /** Custom empty state element (replaces default persona/video empty state) */
  emptyState?: ReactNode;
  /** Show SentContextBadges on user messages (default: true) */
  showContextBadges?: boolean;
  /** Render raw image/video URLs as inline thumbnails in Banan/Vid chats. */
  mediaAutoPreview?: boolean;
  /** Saga renders its own per-turn micro-plan timeline. */
  hideTypingIndicator?: boolean;
}

export function getVisibleMessages(messages: UIMessage[]) {
  return messages
    .filter((msg) => {
      if (msg.role === "user") {
        const text = extractMessageText(msg);
        if (isHiddenControlMessage(text)) return false;
      }
      if (msg.role === "assistant" && hasMarcusOnboardingToolPart(msg)) return false;
      return true;
    })
    .filter((msg, idx, arr) => arr.findIndex((m) => m.id === msg.id) === idx);
}

function hasMarcusOnboardingToolPart(msg: UIMessage): boolean {
  return (msg.parts ?? []).some((part) => {
    if (!part || typeof part !== "object") return false;
    const record = part as { type?: unknown; toolName?: unknown };
    if (record.type === "dynamic-tool") return record.toolName === "marcus_show_onboarding_step";
    return record.type === "tool-marcus_show_onboarding_step";
  });
}

function isRenderableNode(
  node: ReactNode | null | undefined | false,
): node is ReactNode {
  return node !== null && node !== undefined && node !== false;
}

function CompactEmptyState({ emptyState }: { emptyState?: ReactNode }) {
  if (emptyState) return <>{emptyState}</>;
  return (
    <div className="ds-caption text-muted-foreground/40 italic py-4 text-center">
      Start a conversation...
    </div>
  );
}

function DefaultEmptyState({
  emptyState,
  avatarEnabled,
  persona,
}: {
  emptyState?: ReactNode;
  avatarEnabled?: boolean;
  persona?: { humanName: string; humanVideo?: string };
}) {
  if (emptyState) return <>{emptyState}</>;
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-4 text-muted-foreground px-6 pointer-events-none">
      {!avatarEnabled && persona && (
        <div className="w-24 h-24 rounded-full overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.1)] border-4 border-background/50 mb-2 relative">
          {persona.humanVideo ? (
            <video
              src={persona.humanVideo}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover scale-[1.3] opacity-80"
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <Bot className="w-10 h-10 opacity-50" />
            </div>
          )}
        </div>
      )}
      <div>
        <p className="ds-body-strong text-foreground/90">Starting your session...</p>
        <p className="ds-body-sm mt-1 max-w-[240px] mx-auto text-muted-foreground">
          Your co-agent is warming up.
        </p>
      </div>
    </div>
  );
}

function ChatErrorBlock({
  chatError,
  onRetry,
}: {
  chatError?: Error;
  onRetry?: () => void;
}) {
  if (!chatError) return null;
  return (
    <div className="ds-body-sm flex flex-col gap-1 text-red-400 bg-red-500/10 border border-red-500/20 px-4 py-3 rounded-2xl rounded-bl-sm w-fit max-w-[85%] break-words [overflow-wrap:anywhere]">
      <div className="flex items-center gap-2 min-w-0">
        <AlertCircle className="w-4 h-4 shrink-0" />
        <span className="min-w-0">Something went wrong.</span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="ml-1 underline hover:text-red-300 transition-colors"
          >
            Retry
          </button>
        )}
      </div>
      {chatError.message && chatError.message !== "Something went wrong." && (
        <p className="text-xs text-red-400/70 pl-6 break-all">{chatError.message}</p>
      )}
    </div>
  );
}

export function ChatMessageList({
  messages,
  persona,
  avatarEnabled,
  isLoading,
  chatError,
  onRetry,
  compact,
  renderToolPart,
  emptyState,
  showContextBadges = true,
  mediaAutoPreview,
  hideTypingIndicator = false,
}: ChatMessageListProps) {
  const visibleMessages = getVisibleMessages(messages);

  if (compact) {
    if (visibleMessages.length === 0) {
      return <CompactEmptyState emptyState={emptyState} />;
    }
    return (
      <>
        {visibleMessages.map((msg) => {
          const isUser = msg.role === "user";
          const renderedParts = (msg.parts ?? [])
            .map((part, i) =>
              renderChatMessagePart({
                part,
                index: i,
                isUser,
                compact: true,
                mediaAutoPreview,
                renderToolPart,
              }),
            )
            .filter(isRenderableNode);

          if (renderedParts.length === 0) return null;

          const showEditHint =
            !isUser && mediaAutoPreview && messageHasGeneratedMedia(msg);

          return (
            <div
              key={msg.id}
              className={`flex max-w-full min-w-0 flex-col ${isUser ? "items-end" : "items-start"}`}
            >
              {renderedParts}
              {isUser && <SentContextBadges messageId={msg.id} compact />}
              {showEditHint && <EditPromptHint compact />}
            </div>
          );
        })}
      </>
    );
  }

  if (visibleMessages.length === 0) {
    return (
      <DefaultEmptyState
        emptyState={emptyState}
        avatarEnabled={avatarEnabled}
        persona={persona}
      />
    );
  }

  return (
    <>
      {visibleMessages.map((msg) => {
        const isUser = msg.role === "user";
        return (
          <div
            key={msg.id}
            className={`flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`}
          >
            {msg.role === "assistant" && persona && (
              <span className="text-[11px] text-muted-foreground font-semibold px-1 tracking-wide uppercase ml-2">
                {persona.humanName}
              </span>
            )}
            <div className={`max-w-[85%] ${isUser ? "order-first" : ""}`}>
              {(msg.parts ?? []).map((part, i) =>
                renderChatMessagePart({
                  part,
                  index: i,
                  isUser,
                  compact: false,
                  mediaAutoPreview,
                  renderToolPart,
                }),
              )}
              {showContextBadges && isUser && <SentContextBadges messageId={msg.id} />}
              {!isUser && mediaAutoPreview && messageHasGeneratedMedia(msg) && (
                <EditPromptHint />
              )}
            </div>
          </div>
        );
      })}
      <ChatTypingIndicator
        messages={messages}
        isLoading={isLoading}
        hideTypingIndicator={hideTypingIndicator}
        name={persona?.humanName ?? "Agent"}
      />
      <ChatErrorBlock chatError={chatError} onRetry={onRetry} />
    </>
  );
}
